import React, { useState, useEffect, useMemo } from "react";
import Table from "react-bootstrap/Table";
import MetricFormatter from "dashboards/MetricFormatter";
import Pagination from "utils/Pagination";

const LiteKeywordTable = (props) => {
  const [sortBy, setSortBy] = useState(props.keyword_sort);
  const [sortOrderAsc, setSortOrderAsc] = useState(props.keyword_sort_order_asc);
  const [sortedKeywords, setSortedKeywords] = useState([]);
  const keywordCount = props.keywords ? props.keywords.length : 0;
  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const sortKeywords = (by, initial = false) => {
    let kwCopy = initial ? [...props.keywords] : [...sortedKeywords];
    let tempSortOrder = sortOrderAsc;

    if (by === sortBy && !initial) {
      tempSortOrder = !tempSortOrder;
      kwCopy.reverse();
    } else {
      tempSortOrder = initial ? sortOrderAsc : true;
      if (by === "keyword") {
        kwCopy.sort((a, b) => a.keyword.toLowerCase().localeCompare(b.keyword.toLowerCase()));
      } else if (by === "prevpos") {
        kwCopy.sort((a, b) => {
          a.start_date_ranking = typeof a.start_date_ranking === "string" ? parseFloat(a.start_date_ranking) : a.start_date_ranking;
          b.start_date_ranking = typeof b.start_date_ranking === "string" ? parseFloat(b.start_date_ranking) : b.start_date_ranking;

          if (a.start_date_ranking === 0) return 1;
          if (b.start_date_ranking === 0) return -1;
          return a.start_date_ranking - b.start_date_ranking;
        });
      } else if (by === "currentpos") {
        kwCopy.sort((a, b) => {
          if (a.end_date_ranking === 0) return 1;
          if (b.end_date_ranking === 0) return -1;
          return a.end_date_ranking - b.end_date_ranking;
        });
      } else if (by === "diff") {
        kwCopy.sort((a, b) => a.ranking_difference - b.ranking_difference);
      } else if (by === "cpc") {
        kwCopy.sort((a, b) => parseFloat(a.cpc) - parseFloat(b.cpc));
      } else if (by === "volume") {
        kwCopy.sort((a, b) => parseFloat(a.volume) - parseFloat(b.volume));
      } else if (by === "difficulty") {
        kwCopy.sort((a, b) => parseFloat(a.difficulty) - parseFloat(b.difficulty));
      }
      if (!tempSortOrder) {
        kwCopy.reverse();
      }
    }

    setSortBy(by);
    setSortOrderAsc(tempSortOrder);
    setSortedKeywords(kwCopy);
  };

  useEffect(() => {
    if (props.keywords && props.keywords.length > 0) {
      sortKeywords(sortBy, true);
    }
  }, [props.keywords]); // Run this effect when props.keywords change


  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * itemsPerPage;
    const lastPageIndex = firstPageIndex + itemsPerPage;
    return sortedKeywords.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, sortedKeywords]); // Add sortedKeywords as a dependency

  const sortUpHtml = <i className="fas fa-sort-up"></i>;
  const sortDownHtml = <i className="fas fa-sort-down"></i>;

  return (
    <div className="keyword-table-container">
      <div className="keyword-table-header">Keyword Ranking</div>
      <div className="keyword-ranking-table">
        <Table className="table-white">
          <thead>
            <tr>
              {props.metrics.includes("keywordName") && (
                <th onClick={() => sortKeywords("keyword")}>
                  Keyword&nbsp;
                  {sortBy === "keyword" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordSerpFeatures") && <th className="nosort">SERP Features</th>}
              {props.metrics.includes("keywordPreviousPosition") && (
                <th onClick={() => sortKeywords("prevpos")}>
                  Prev. Pos&nbsp;
                  {sortBy === "prevpos" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordCurrentPosition") && (
                <th onClick={() => sortKeywords("currentpos")}>
                  Current Pos&nbsp;
                  {sortBy === "currentpos" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordDifference") && (
                <th onClick={() => sortKeywords("diff")}>
                  Diff&nbsp;
                  {sortBy === "diff" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordCostPerConversion") && (
                <th onClick={() => sortKeywords("cpc")}>
                  CPC&nbsp;
                  {sortBy === "cpc" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordSearchVolume") && (
                <th onClick={() => sortKeywords("volume")}>
                  Volume&nbsp;
                  {sortBy === "volume" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordDifficulty") && (
                <th onClick={() => sortKeywords("difficulty")}>
                  Difficulty&nbsp;
                  {sortBy === "difficulty" ? (sortOrderAsc ? sortUpHtml : sortDownHtml) : null}
                </th>
              )}
              {props.metrics.includes("keywordLabel") && <th className="nosort">Label</th>}
              {props.metrics.includes("keywordUrl") && <th className="nosort rankingPage">Ranking Page</th>}
            </tr>
          </thead>
          <tbody>
            {currentTableData.map((keyword, i) => {
              let diffBackground = "";
              if (keyword.ranking_difference > 0) diffBackground = "bg-success text-white";
              else if (keyword.ranking_difference < 0) diffBackground = "bg-danger text-white";

              return (
                <tr key={keyword.keyword + i}>
                  {props.metrics.includes("keywordName") && (
                    <td className="keyword-left">
                      <div className="keyword-table-row">{keyword.keyword}</div>
                    </td>
                  )}
                  {props.metrics.includes("keywordSerpFeatures") && (
                    <td>
                      <div className="keyword-table-row">
                        {keyword.serp_features.map((serp_feature) => {
                          if (serp_feature === "rel") return <i key={serp_feature} className="fas fa-question"></i>;
                          if (serp_feature === "rev") return <i key={serp_feature} className="fas fa-star"></i>;
                          if (serp_feature === "fsn") return <i key={serp_feature} className="fas fa-crown"></i>;
                          if (serp_feature === "shp") return <i key={serp_feature} className="fas fa-shopping-cart"></i>;
                          if (serp_feature === "stl") return <i key={serp_feature} className="fas fa-link"></i>;
                          if (serp_feature === "adt" || serp_feature === "adb")
                            return <i key={serp_feature} className="fas fa-ad"></i>;
                          if (serp_feature === "vid") return <i key={serp_feature} className="fab fa-youtube"></i>;
                          if (serp_feature === "img") return <i key={serp_feature} className="far fa-images"></i>;
                          if (serp_feature === "geo") return <i key={serp_feature} className="fas fa-globe"></i>;
                          if (serp_feature === "vib") return <i key={serp_feature} className="fas fa-video"></i>;
                          if (serp_feature === "new") return <i key={serp_feature} className="fas fa-newspaper"></i>;
                          if (serp_feature === "twt") return <i key={serp_feature} className="fab fa-twitter-square"></i>;
                          if (serp_feature === "knw") return <i key={serp_feature} className="fas fa-bolt"></i>;
                          if (serp_feature === "kng") return <i key={serp_feature} className="far fa-circle"></i>;
                          return null;
                        })}
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordPreviousPosition") && (
                    <td className="keyword-centered">
                      <div className="keyword-table-row">
                        {keyword.start_date_ranking === 0 ? "100+" : <MetricFormatter.Sessions value={keyword.start_date_ranking} />}
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordCurrentPosition") && (
                    <td className="keyword-centered">
                      <div className="keyword-table-row">
                        {keyword.end_date_ranking === 0 ? "100+" : <MetricFormatter.Sessions value={keyword.end_date_ranking} />}
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordDifference") && (
                    <td className={`${diffBackground} keyword-centered`}>
                      <div className="keyword-table-row">
                        <MetricFormatter.Sessions value={keyword.ranking_difference} />
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordCostPerConversion") && (
                    <td>
                      <div className="keyword-table-row">
                        <MetricFormatter.CPC value={isNaN(parseFloat(keyword.cpc)) ? 0 : parseFloat(keyword.cpc)} />
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordSearchVolume") && (
                    <td className="keyword-centered">
                      <div className="keyword-table-row">
                        <MetricFormatter.Sessions value={isNaN(parseFloat(keyword.volume)) ? 0 : parseFloat(keyword.volume)} />
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordDifficulty") && (
                    <td>
                      <div className="keyword-table-row">
                        <MetricFormatter.Sessions value={isNaN(parseFloat(keyword.difficulty)) ? 0 : parseFloat(keyword.difficulty)} />
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordLabel") && (
                    <td>
                      <div className="keyword-table-row">
                        {keyword.tag &&
                          keyword.tag.map((el, index) => {
                            let string = el;
                            if (index < keyword.tag.length - 1) string += ", ";
                            return string;
                          })}
                      </div>
                    </td>
                  )}
                  {props.metrics.includes("keywordUrl") && (
                    <td className="keyword-right" style={{ fontSize: "0.8rem" }}>
                      <div className="keyword-table-row rankingPage">
                        {keyword.ranking_page && (
                          <a href={keyword.ranking_page} target="_blank" rel="noopener noreferrer">
                            <i style={{ marginRight: "10px" }} className="fas fa-external-link-alt"></i>
                          </a>
                        )}
                        {keyword.ranking_page}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Pagination
        className="pagination-bar"
        currentPage={currentPage}
        totalCount={keywordCount}
        pageSize={itemsPerPage}
        onPageChange={page => setCurrentPage(page)}
      />
    </div>
  );
};

export default LiteKeywordTable;
